import React, { useEffect, useMemo, useState } from 'react';
import { BoxPlotOutlined, CheckCircleFilled, FileOutlined, FilePdfOutlined, InfoCircleOutlined, } from '@ant-design/icons';
import { Button, Checkbox, Empty, Modal, Space, Tag, Tooltip, Typography } from 'antd';
const { Text, Paragraph } = Typography;
function formatBytes(bytes) {
    if (bytes < 1024) {
        return `${bytes} B`;
    }
    if (bytes < 1024 * 1024) {
        return `${(bytes / 1024).toFixed(1)} KB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
function fileExtension(filename) {
    const parts = filename.split('.');
    return parts.length > 1 ? parts[parts.length - 1].toUpperCase() : 'FILE';
}
const listContainerStyle = {
    maxHeight: 280,
    overflow: 'auto',
    border: '1px solid #f0f0f0',
    borderRadius: 8,
    background: '#fafafa',
};
const EmbeddedAttachmentsModal = ({ visible, attachments, loading, onExtract, onSkip, queuePosition = 1, queueTotal = 1, partLabel, }) => {
    const extractable = useMemo(() => attachments.filter(item => item.is_3d), [attachments]);
    const unsupported = useMemo(() => attachments.filter(item => !item.is_3d), [attachments]);
    const [selected, setSelected] = useState([]);
    useEffect(() => {
        if (visible) {
            setSelected(extractable.map(item => item.filename));
        }
    }, [visible, extractable]);
    const allExtractableSelected = extractable.length > 0 && selected.length === extractable.length;
    const toggleFile = (filename, is3d) => {
        if (!is3d || loading) {
            return;
        }
        setSelected(prev => prev.includes(filename)
            ? prev.filter(name => name !== filename)
            : [...prev, filename]);
    };
    const primaryLabel = selected.length === 1
        ? 'Add 1 file to part'
        : `Add ${selected.length} files to part`;
    const modalTitle = (React.createElement(Space, { align: "start", size: 12 },
        React.createElement(FilePdfOutlined, { style: { fontSize: 22, color: '#cf1322', marginTop: 2 } }),
        React.createElement("div", null,
            React.createElement("div", { style: { fontWeight: 600, fontSize: 16, lineHeight: 1.3 } },
                "3D files found in your PDF",
                queueTotal > 1 ? (React.createElement(Tag, { color: "processing", style: { marginLeft: 8, verticalAlign: 'middle' } },
                    "Part ",
                    queuePosition,
                    " of ",
                    queueTotal)) : null),
            React.createElement(Text, { type: "secondary", style: { fontSize: 13 } },
                partLabel ? (React.createElement(React.Fragment, null,
                    React.createElement(Text, { strong: true }, partLabel),
                    ' — ')) : null,
                "Choose which embedded models to attach to this part."))));
    const renderFileRow = (item, selectable) => {
        const isSelected = selected.includes(item.filename);
        const rowStyle = {
            display: 'flex',
            alignItems: 'flex-start',
            gap: 12,
            padding: '10px 12px',
            marginBottom: 8,
            borderRadius: 8,
            border: `1px solid ${isSelected ? '#91caff' : '#f0f0f0'}`,
            background: isSelected ? '#e6f4ff' : '#fff',
            cursor: selectable && !loading ? 'pointer' : 'default',
            opacity: selectable ? 1 : 0.72,
            transition: 'border-color 0.15s ease, background 0.15s ease',
        };
        return (React.createElement("div", { key: item.filename, style: rowStyle, onClick: () => toggleFile(item.filename, selectable), role: selectable ? 'button' : undefined, tabIndex: selectable ? 0 : undefined, onKeyDown: event => {
                if (!selectable) {
                    return;
                }
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    toggleFile(item.filename, selectable);
                }
            } },
            selectable ? (React.createElement(Checkbox, { checked: isSelected, disabled: loading, onClick: event => event.stopPropagation(), onChange: () => toggleFile(item.filename, true), style: { marginTop: 2 } })) : (React.createElement(FileOutlined, { style: { color: '#8c8c8c', fontSize: 16, marginTop: 3 } })),
            React.createElement("div", { style: { flex: 1, minWidth: 0 } },
                React.createElement(Space, { size: 6, wrap: true },
                    selectable ? (React.createElement(BoxPlotOutlined, { style: { color: '#389e0d' } })) : (React.createElement(FileOutlined, { style: { color: '#8c8c8c' } })),
                    React.createElement(Tooltip, { title: item.filename, placement: "topLeft" },
                        React.createElement(Text, { strong: true, ellipsis: true, style: { maxWidth: 320 } }, item.filename)),
                    React.createElement(Tag, { color: selectable ? 'green' : 'default', style: { margin: 0 } }, fileExtension(item.filename))),
                React.createElement("div", { style: { marginTop: 4 } },
                    React.createElement(Text, { type: "secondary", style: { fontSize: 12 } },
                        formatBytes(item.size_bytes),
                        selectable ? ' · Ready to add' : ' · Not a supported 3D type'))),
            isSelected && selectable ? (React.createElement(CheckCircleFilled, { style: { color: '#1677ff', fontSize: 16, marginTop: 2 } })) : null));
    };
    return (React.createElement(Modal, { title: modalTitle, open: visible, onCancel: onSkip, centered: true, width: 560, destroyOnClose: true, maskClosable: !loading, closable: !loading, footer: React.createElement("div", { style: {
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 12,
            } },
            React.createElement(Text, { type: "secondary", style: { fontSize: 13 } }, extractable.length > 0
                ? `${selected.length} of ${extractable.length} selected`
                : 'No supported 3D files'),
            React.createElement(Space, null,
                React.createElement(Button, { onClick: onSkip, disabled: loading }, "Not now"),
                React.createElement(Button, { type: "primary", loading: loading, disabled: !extractable.length || !selected.length, onClick: () => onExtract(selected) }, primaryLabel))) }, extractable.length > 0 ? (React.createElement(React.Fragment, null,
        React.createElement("div", { style: { marginBottom: 12 } },
            React.createElement(Space, { size: 8, wrap: true },
                React.createElement(Tag, { color: "success", icon: React.createElement(BoxPlotOutlined, null) },
                    extractable.length,
                    " 3D file",
                    extractable.length !== 1 ? 's' : ''),
                unsupported.length > 0 ? (React.createElement(Tag, { icon: React.createElement(InfoCircleOutlined, null) },
                    unsupported.length,
                    " other attachment",
                    unsupported.length !== 1 ? 's' : '')) : null)),
        React.createElement(Paragraph, { type: "secondary", style: { marginBottom: 12, fontSize: 13 } }, "We detected CAD models embedded inside your drawing PDF. Select the files you want in the 3D section for this part."),
        React.createElement("div", { style: { ...listContainerStyle, padding: 10 } },
            React.createElement("div", { style: {
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '0 4px 10px',
                    marginBottom: 4,
                    borderBottom: '1px solid #f0f0f0',
                } },
                React.createElement(Text, { strong: true, style: { fontSize: 13 } }, "3D files"),
                React.createElement(Checkbox, { checked: allExtractableSelected, indeterminate: selected.length > 0 && !allExtractableSelected, disabled: loading, onChange: event => setSelected(event.target.checked
                        ? extractable.map(item => item.filename)
                        : []) }, "Select all")),
            extractable.map(item => renderFileRow(item, true))),
        unsupported.length > 0 ? (React.createElement("div", { style: { marginTop: 16 } },
            React.createElement(Text, { type: "secondary", style: { fontSize: 13, display: 'block', marginBottom: 8 } }, "Other embedded files (cannot be added as 3D)"),
            React.createElement("div", { style: { ...listContainerStyle, padding: 10 } }, unsupported.map(item => renderFileRow(item, false))))) : null)) : (React.createElement(Empty, { image: Empty.PRESENTED_IMAGE_SIMPLE, description: React.createElement("div", null,
            React.createElement(Text, null, "This PDF has embedded files, but none match a supported 3D format."),
            React.createElement("br", null),
            React.createElement(Text, { type: "secondary", style: { fontSize: 13 } }, "Upload a STEP, STP, or other supported 3D file directly instead.")) }))));
};
export default EmbeddedAttachmentsModal;
